import { PropsWithChildren } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { DropdownMenu, MoreButton, Text } from '@ingrid/luterkort-react';

import { RoleGuard } from '~src/components/RoleGuard/RoleGuard';
import { useTranslation } from '~src/i18n/use-translation';
import { useSiteId } from '~src/main/hooks/useSiteId';
import { useToast } from '~src/main/hooks/useToast';
import { AppRoute } from '~src/main/routes';

import { useCarrierContractRemoveMutation } from './mutations';

interface RootProps extends PropsWithChildren {
  disabled?: boolean;
  listing?: boolean;
}

interface LinkProps {
  contractId: string;
}

interface ActionProp {
  onClick: () => void;
  disabled?: boolean;
}

type DeleteItemProps = LinkProps & {
  onSuccess?: () => void;
};

function Root({ children, listing = false, disabled = false }: RootProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <MoreButton size={listing ? 'sm' : 'md'} variant={listing ? 'quaternary' : 'secondary'} disabled={disabled} />
      </DropdownMenu.Trigger>

      {!disabled && <DropdownMenu.Content>{children}</DropdownMenu.Content>}
    </DropdownMenu.Root>
  );
}

function ShowDetailItem({ contractId }: LinkProps) {
  const { t } = useTranslation();
  const siteId = useSiteId();

  return (
    <DropdownMenu.Item asChild>
      <RouterLink to={`/${AppRoute.SITE}/${siteId}/${AppRoute.SETTINGS}/${AppRoute.CARRIER_CONTRACTS}/${contractId}`}>
        {t('common.showDetails')}
      </RouterLink>
    </DropdownMenu.Item>
  );
}

function ClearItem({ onClick, disabled = false }: ActionProp) {
  const { t } = useTranslation();

  return (
    <DropdownMenu.Item onClick={onClick} disabled={disabled}>
      {t('common.clear')}
    </DropdownMenu.Item>
  );
}

function EditItem({ contractId }: LinkProps) {
  const siteId = useSiteId();
  const { t } = useTranslation();

  return (
    <RoleGuard roles={['transport:edit']} withTooltip>
      <DropdownMenu.Item asChild>
        <RouterLink
          to={`/${AppRoute.SITE}/${siteId}/${AppRoute.SETTINGS}/${AppRoute.CARRIER_CONTRACTS}/${AppRoute.EDIT}/${contractId}`}
        >
          {t('common.edit')}
        </RouterLink>
      </DropdownMenu.Item>
    </RoleGuard>
  );
}

function DuplicateItem({ contractId }: LinkProps) {
  const siteId = useSiteId();
  const { t } = useTranslation();

  return (
    <RoleGuard roles={['transport:edit']} withTooltip>
      <DropdownMenu.Item asChild>
        <RouterLink
          to={`/${AppRoute.SITE}/${siteId}/${AppRoute.SETTINGS}/${AppRoute.CARRIER_CONTRACTS}/${AppRoute.DUPLICATE}/${contractId}`}
        >
          {t('common.duplicate')}
        </RouterLink>
      </DropdownMenu.Item>
    </RoleGuard>
  );
}

function DeleteItem({ contractId, onSuccess }: DeleteItemProps) {
  const mutation = useCarrierContractRemoveMutation();

  const { t } = useTranslation();
  const { toast } = useToast();

  async function onClick() {
    const promise = mutation.mutateAsync(contractId);

    await toast.promise(promise, {
      loading: { title: t('carrierContracts.details.removeLoading') },
      error: { title: t('carrierContracts.details.removeError') },
      success: { title: t('carrierContracts.details.removeSuccess') },
    });

    onSuccess?.();
  }

  return (
    <RoleGuard roles={['transport:edit']} withTooltip>
      <DropdownMenu.Item onClick={onClick}>
        <Text variant="critical">{t('common.delete')}</Text>
      </DropdownMenu.Item>
    </RoleGuard>
  );
}

export const Menu = Object.assign(Root, {
  ShowDetailItem,
  ClearItem,
  EditItem,
  DuplicateItem,
  DeleteItem,
});
